import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';
import { rolesService } from './roles.service';
import { UserById } from './getUserById.service';

// GraphQL mutations and queries
export const LOGIN_MUTATION = gql`
  mutation LoginBackoffice($loginInput: LoginInput!) {
    loginBackoffice(loginInput: $loginInput) {
      accessToken
      user {
        id
        username
        name
        email
        phone
        role {
          id
          name
        }
        environment {
          id
          name
          host
        }
        credit
        parentId
        isActive
        comment
        lastLogin
        loginIp
        avatarUrl
        language
        timezone
        Balance {
          id
          amount
          credit
          onHold
        }
        createdAt
        updatedAt
        isWagerEnabled
      }
    }
  }
`;

// Types for authentication
export interface LoginInput {
  username: string;
  password: string;
}

export interface Role {
  id: number;
  name: string;
}

export interface Balance {
  id: number;
  amount: number;
  credit: number;
  onHold: number;
}

export interface Environment {
  id?: number;
  name?: string;
  host: string;
}

export interface LoginResponse {
  accessToken: string;
  user: {
    id: number;
    username: string;
    name?: string;
    email?: string;
    phone?: string;
    role: Role;
    environment?: Environment;
    credit: number;
    parentId?: number;
    isActive: boolean;
    comment?: string;
    lastLogin?: string;
    loginIp?: string;
    avatarUrl?: string;
    language?: string;
    timezone?: string;
    Balance?: Balance;
    createdAt: string;
    updatedAt: string;
  };
}

export interface User {
  id: number;
  username: string;
  name?: string;
  email?: string;
  phone?: string;
  role: Role;
  environment?: Environment;
  credit: number;
  parentId?: number;
  isActive: boolean;
  comment?: string;
  lastLogin?: string;
  loginIp?: string;
  avatarUrl?: string;
  language?: string;
  timezone?: string;
  Balance?: Balance;
  createdAt: string;
  updatedAt: string;
  isWagerEnabled?: boolean;
}

// Auth service class
export class AuthService {
  private static instance: AuthService;
  private currentUser: User | UserById | null = null;
  private roles: any[] = [];

  private constructor() {
    // Initialize current user from localStorage on service creation
    this.initializeUserFromStorage();
    // Initialize roles from localStorage
    this.initializeRolesFromStorage();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Initialize user from localStorage
  private initializeUserFromStorage(): void {
    try {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        this.currentUser = JSON.parse(storedUser);
      }
    } catch (error) {
      console.error('Error initializing user from storage:', error);
      // Clear corrupted data
      localStorage.removeItem('currentUser');
    }
  }

  // Fetch and store roles
  async fetchRoles(): Promise<void> {
    try {
      const roles = await rolesService.getAllRoles();
      this.roles = roles;
      localStorage.setItem('roles', JSON.stringify(roles));
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  // Get role ID by name
  getRoleIdByName(roleName: string): number | null {
    const role = this.roles.find((r) => r.name === roleName);
    return role ? role.id : null;
  }

  // Get all roles
  getRoles(): any[] {
    return this.roles;
  }

  hasPermission(permissionName: string): boolean {
    if (!this.currentUser || !this.currentUser.role) return false;

    const role = this.roles.find((r) => r.id === this.currentUser!.role.id);
    if (!role || !role.permissions) return false;

    return role.permissions.some(
      (p: { name: string }) => p.name === permissionName,
    );
  }

  // Initialize roles from localStorage
  private initializeRolesFromStorage(): void {
    try {
      const storedRoles = localStorage.getItem('roles');
      if (storedRoles) {
        this.roles = JSON.parse(storedRoles);
      }
    } catch (error) {
      console.error('Error initializing roles from storage:', error);
      localStorage.removeItem('roles');
    }
  }

  // Login method
  async login(credentials: LoginInput): Promise<LoginResponse> {
    try {
      const response = await apolloClient.mutate({
        mutation: LOGIN_MUTATION,
        variables: { loginInput: credentials },
      });

      // If there are errors in the response, throw them
      if (response.errors && response.errors.length > 0) {
        throw response;
      }

      // If data or loginBackoffice is missing, throw a generic error
      if (!response.data || !response.data.loginBackoffice) {
        throw new Error('Login failed: No data returned');
      }

      const loginResponse = response.data.loginBackoffice as LoginResponse;

      // Store tokens in localStorage
      localStorage.setItem('authToken', loginResponse.accessToken);

      // Set current user and store in localStorage
      this.currentUser = loginResponse.user;
      localStorage.setItem('currentUser', JSON.stringify(loginResponse.user));

      // Fetch roles after successful login
      await this.fetchRoles();

      return loginResponse;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Logout method
  async logout(): Promise<void> {
    try {
      // Clear tokens and user data
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('roles');
      this.currentUser = null;
      this.roles = [];

      // Clear Apollo cache
      await apolloClient.clearStore();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token && token.length > 0;
  }

  // Get stored user (from cache or localStorage)
  getStoredUser(): User | UserById | null {
    if (this.currentUser) {
      return this.currentUser;
    }

    // Try to get from localStorage if not in memory
    try {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        this.currentUser = JSON.parse(storedUser);
        return this.currentUser;
      }
    } catch (error) {
      console.error('Error getting user from storage:', error);
      localStorage.removeItem('currentUser');
    }

    return null;
  }

  // Set current user (useful after login)
  setCurrentUser(user: User | UserById): void {
    this.currentUser = user;
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  // Check if user has specific role
  hasRole(roleName: string): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.role.name === roleName;
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();
